import { DetailedHTMLProps, FC, IframeHTMLAttributes, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "@mui/material";
import { resizeIframe } from "./injectedScript.js";
import { buildIframeColorModeCss, prependColorModeStyle, serializeDocument } from "./iframeColorMode";

interface ResponsiveIframeProps extends Omit<DetailedHTMLProps<IframeHTMLAttributes<HTMLIFrameElement>, HTMLIFrameElement>, "src"> {
    srcDoc: string;
}

interface ProcessedDocument {
    html: string;
    /** Frees the resize script's object URL — the document that referenced it is gone by then. */
    revoke: () => void;
}

const processDocument = (htmlString: string, iframeId: string, colorModeCss: string): ProcessedDocument => {
    const parser = new DOMParser();
    const tempDocument = parser.parseFromString(htmlString, "text/html");

    // NG-8761: the frame does not inherit the host page's theme — give it its own baseline.
    prependColorModeStyle(tempDocument, colorModeCss);

    // Created a data URL for the script to avoid inline execution
    // Pass the unique iframe ID to the script
    const scriptContent = `(${resizeIframe})("${iframeId}")`;
    const blob = new Blob([scriptContent], { type: "application/javascript" });
    const scriptUrl = URL.createObjectURL(blob);

    const script = document.createElement("script");
    script.src = scriptUrl;
    // Add the data-resize-injected attribute so fallback knows script already exists
    script.setAttribute("data-resize-injected", "true");
    script.setAttribute("data-iframe-id", iframeId);

    const head = tempDocument.querySelector("head");
    head?.appendChild(script);

    // NG-8761: through serializeDocument, so a template that carries `<!DOCTYPE html>` keeps it and the
    // frame renders the stored bytes rather than a doctype-less re-serialization of them. A doctype-less
    // template is returned exactly as before.
    return { html: serializeDocument(tempDocument), revoke: () => URL.revokeObjectURL(scriptUrl) };
};

const ResponsiveIframe: FC<ResponsiveIframeProps> = ({ srcDoc, style, ...rest }) => {
    const [iframeHeight, setIframeHeight] = useState<number>(0);
    const [injectedDocument, setInjectedDocument] = useState<string>("");
    const [iframeRef, setIframeRef] = useState<HTMLIFrameElement | null>(null);
    const theme = useTheme();

    const colorModeCss = useMemo(() => buildIframeColorModeCss(theme), [theme]);
    // NG-8761: read through a ref by the effects that must NOT re-run on a mode change — rebuilding
    // srcDoc reloads the frame (scroll position lost, resize handshake re-run) and re-attaching the
    // load listener is pointless work. The live-swap effect below owns reacting to the mode.
    const colorModeCssRef = useRef<string>(colorModeCss);
    // Synced in an effect, not during render: render has to stay pure, and an abandoned concurrent
    // render must not be able to leave a mode in the ref that was never committed. The initial value
    // comes from useRef above, so first paint needs no sync.
    useEffect(() => {
        colorModeCssRef.current = colorModeCss;
    }, [colorModeCss]);

    // Generate a unique ID for this iframe instance
    const iframeIdRef = useRef<string>(`responsive-iframe-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`);

    useEffect(() => {
        // The baseline is read from the ref so first paint carries it without making the mode a dependency.
        const { html, revoke } = processDocument(srcDoc || "", iframeIdRef.current, colorModeCssRef.current);
        setInjectedDocument(html);

        return () => {
            // Every rebuild minted an object URL for the resize script; nothing referenced it after this.
            revoke();
            setInjectedDocument("");
        };
    }, [srcDoc]);

    // NG-8761: a mode change swaps the baseline's text inside the LIVE document instead of rebuilding
    // srcDoc — no reload, so scroll position and the measured height survive the toggle. This is the
    // re-injection that prependColorModeStyle's replace-don't-skip behaviour exists for.
    useEffect(() => {
        const iframeDoc = iframeRef?.contentDocument;
        if (!iframeDoc) {
            return;
        }

        prependColorModeStyle(iframeDoc, colorModeCss);
        // Not keyed on injectedDocument: that fires before the frame has parsed the new srcDoc, so it
        // would mutate the outgoing document and be discarded. A rebuilt srcDoc carries the baseline
        // itself, and the load handler below re-asserts it.
    }, [iframeRef, colorModeCss]);

    useEffect(() => {
        function onMessage(message) {
            if (message.origin !== window.location.origin) {
                return;
            }

            const { data } = message;
            // Only process messages that have our specific iframe ID
            if (data?.height && data?.width && data?.iframeId === iframeIdRef.current) {
                setIframeHeight(data.height);
            }
        }

        window.addEventListener("message", onMessage);

        return () => window.removeEventListener("message", onMessage);
    }, []);

    // Fallback approach: If the injected script doesn't work,
    // try to inject it directly after the iframe loads
    useEffect(() => {
        if (!iframeRef) {
            return undefined;
        }

        const handleLoad = () => {
            try {
                const iframeDoc = iframeRef.contentDocument || iframeRef.contentWindow?.document;
                if (iframeDoc && !iframeDoc.querySelector(`script[data-iframe-id="${iframeIdRef.current}"]`)) {
                    const script = iframeDoc.createElement("script");
                    script.setAttribute("data-resize-injected", "true");
                    script.setAttribute("data-iframe-id", iframeIdRef.current);
                    script.textContent = `(${resizeIframe})("${iframeIdRef.current}")`;
                    iframeDoc.head?.appendChild(script);
                }

                // NG-8761: same fallback reasoning as the script above — replaces the baseline srcDoc
                // carried, which is how a frame that loaded before a mode change catches up.
                if (iframeDoc) {
                    prependColorModeStyle(iframeDoc, colorModeCssRef.current);
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.debug("Could not inject script directly into iframe:", error);
            }
        };

        iframeRef.addEventListener("load", handleLoad);
        return () => iframeRef.removeEventListener("load", handleLoad);
    }, [iframeRef]);

    return (
        <iframe
            ref={setIframeRef}
            title="Preview"
            srcDoc={injectedDocument}
            style={{
                top: 0,
                left: 0,
                width: "100%",
                height: iframeHeight || "100%",
                border: "none",
                // NG-8761: the element itself paints white until srcDoc renders — match the theme to avoid a flash.
                backgroundColor: theme.palette.background.paper,
                ...style,
            }}
            {...rest}
        />
    );
};

export default ResponsiveIframe;
